import { useState, useEffect, useRef, useCallback } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Menu, X, Phone, Search, Heart, Users, GraduationCap, LockKeyhole } from "lucide-react";

const ICON_URL = "https://customer-assets.emergentagent.com/job_online-pflegekasse/artifacts/72f9n7x9_1775301634776.png";

const pflegeberatungLinks = [
  { label: "Leistungen", href: "#leistungen" },
  { label: "So geht's", href: "#so-gehts" },
  { label: "Termin buchen", href: "#termin-buchen" },
  { label: "FAQ", href: "#faq" },
  { label: "Kontakt", href: "#kontakt" },
];

/* ── Sliding Toggle Component ── */
function AreaSwitcher({ isAlltag, size = "md", onSwitch, idSuffix = "" }) {
  const isMd = size === "md";
  const navigate = useNavigate();

  const handleSwitch = (e, toAlltag) => {
    if (onSwitch) onSwitch();
    // Allow ctrl/cmd+click for new tab
    if (e.metaKey || e.ctrlKey) return;
    e.preventDefault();
    navigate(toAlltag ? "/alltagsbegleitung" : "/");
  };

  return (
    <div
      className={`relative flex items-center rounded-full bg-slate-100 border border-slate-200/80 ${isMd ? "p-[3px]" : "p-[3px]"}`}
      data-testid={`area-switcher${idSuffix}`}
      role="tablist"
      aria-label="Bereichs-Auswahl"
    >
      {/* Sliding indicator */}
      <div
        className={`absolute top-[3px] bottom-[3px] rounded-full transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)] shadow-sm w-[calc(50%-3px)] ${
          isAlltag ? "bg-green-600" : "bg-cyan-700"
        }`}
        style={{
          transform: isAlltag ? "translateX(3px)" : "translateX(calc(100% + 3px))",
        }}
      />

      {/* Alltagsbegleitung */}
      <Link
        to="/alltagsbegleitung"
        onClick={(e) => handleSwitch(e, true)}
        data-testid={`switch-alltag${idSuffix}`}
        role="tab"
        aria-selected={isAlltag}
        className={`relative z-10 flex items-center justify-center gap-1.5 rounded-full font-semibold transition-colors duration-200 select-none ${
          isMd ? "px-4 py-2 text-[13px]" : "px-3 py-1.5 text-xs"
        } ${
          isAlltag ? "text-white" : "text-slate-500 hover:text-green-700"
        }`}
      >
        <Heart className={isMd ? "h-3.5 w-3.5" : "h-3 w-3"} />
        <span>Alltagsbegleitung</span>
      </Link>

      {/* Pflegeberatung */}
      <Link
        to="/"
        onClick={(e) => handleSwitch(e, false)}
        data-testid={`switch-pflege${idSuffix}`}
        role="tab"
        aria-selected={!isAlltag}
        className={`relative z-10 flex items-center justify-center gap-1.5 rounded-full font-semibold transition-colors duration-200 select-none ${
          isMd ? "px-4 py-2 text-[13px]" : "px-3 py-1.5 text-xs"
        } ${
          !isAlltag ? "text-white" : "text-slate-500 hover:text-cyan-700"
        }`}
      >
        <Users className={isMd ? "h-3.5 w-3.5" : "h-3 w-3"} />
        <span>Pflegeberatung</span>
      </Link>
    </div>
  );
}

/* ── Mobile Switcher with Swipe ── */
function MobileSwitcherBar({ isAlltag }) {
  const navigate = useNavigate();
  const barRef = useRef(null);
  const touchStart = useRef(null);

  const handleSwipe = useCallback((direction) => {
    if (direction === "left" && !isAlltag) {
      // Swipe left → Alltagsbegleitung
      navigate("/alltagsbegleitung");
    } else if (direction === "right" && isAlltag) {
      // Swipe right → Pflegeberatung
      navigate("/");
    }
  }, [isAlltag, navigate]);

  useEffect(() => {
    const el = barRef.current;
    if (!el) return;

    const onTouchStart = (e) => {
      touchStart.current = e.touches[0].clientX;
    };
    const onTouchEnd = (e) => {
      if (touchStart.current === null) return;
      const diff = e.changedTouches[0].clientX - touchStart.current;
      if (Math.abs(diff) > 50) {
        handleSwipe(diff < 0 ? "left" : "right");
      }
      touchStart.current = null;
    };

    el.addEventListener("touchstart", onTouchStart, { passive: true });
    el.addEventListener("touchend", onTouchEnd, { passive: true });
    return () => {
      el.removeEventListener("touchstart", onTouchStart);
      el.removeEventListener("touchend", onTouchEnd);
    };
  }, [handleSwipe]);

  return (
    <div ref={barRef} className="xl:hidden flex justify-center py-2 px-4" data-testid="mobile-switcher-bar">
      <AreaSwitcher isAlltag={isAlltag} size="sm" idSuffix="-mobile" />
    </div>
  );
}

export default function Header() {
  const [mobileOpen, setMobileOpen] = useState(false);
  const location = useLocation();
  const isAlltag = location.pathname.startsWith("/alltagsbegleitung") || location.pathname.startsWith("/bewerbung");

  const scrollTo = (href) => {
    setMobileOpen(false);
    if (location.pathname !== "/" && !href.startsWith("/")) {
      window.location.href = "/" + href;
      return;
    }
    const el = document.querySelector(href);
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <header data-testid="header" className="fixed top-0 w-full z-50 bg-white/85 backdrop-blur-xl border-b border-slate-200/50 transition-all">
      <div className="max-w-7xl 2xl:max-w-[1500px] min-[1800px]:max-w-[1800px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16 xl:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-0 shrink-0 group" data-testid="header-logo">
            <div className="h-10 w-10 sm:h-12 sm:w-12 xl:h-14 xl:w-14 shrink-0 overflow-hidden">
              <img src={ICON_URL} alt="Lehnen & Weinacht Logo" className="h-full w-auto object-cover object-left scale-[2.3] origin-[15%_41%]" />
            </div>
            <div className="ml-2 sm:ml-3">
              <span className="text-base sm:text-lg xl:text-xl font-extrabold text-slate-900 font-['Manrope'] leading-none block tracking-tight group-hover:text-cyan-700 transition-colors">
                Lehnen & Weinacht
              </span>
              <span className="text-[9px] sm:text-[10px] xl:text-[11px] font-semibold text-cyan-700 tracking-[0.1em] sm:tracking-[0.12em] uppercase mt-0.5 block">
                Pflegeberatung & Alltagsbegleitung
              </span>
            </div>
          </Link>

          {/* Desktop: Area switcher + nav */}
          <div className="hidden xl:flex items-center gap-1">
            <div className="mr-3">
              <AreaSwitcher isAlltag={isAlltag} size="md" />
            </div>

            {!isAlltag && (
              <nav className="hidden min-[1800px]:flex items-center gap-0" data-testid="desktop-nav">
                {pflegeberatungLinks.map((link) => (
                  <button key={link.href} onClick={() => scrollTo(link.href)}
                    className="px-2.5 py-2 text-[13px] font-medium text-slate-600 hover:text-cyan-700 rounded-lg hover:bg-cyan-50/60 transition-all whitespace-nowrap"
                    data-testid={`nav-${link.href.replace('#', '')}`}>
                    {link.label}
                  </button>
                ))}
              </nav>
            )}

            <Link to="/blog/pflegeleistungen-finder" data-testid="nav-finder"
              className="flex items-center gap-1.5 ml-1 px-3 py-2 text-[13px] font-semibold text-cyan-700 bg-cyan-50 hover:bg-cyan-100 rounded-lg transition-all whitespace-nowrap">
              <Search className="h-3.5 w-3.5" />
              Leistungen prüfen
            </Link>
            <Link to="/schulungen" data-testid="nav-schulungen"
              className={`flex items-center gap-1.5 ml-1 px-3 py-2 text-[13px] font-semibold rounded-lg transition-all whitespace-nowrap ${location.pathname.startsWith("/schulungen") ? "text-cyan-700 bg-cyan-50" : "text-slate-600 hover:text-cyan-700 hover:bg-cyan-50/60"}`}>
              <GraduationCap className="h-3.5 w-3.5" />
              Schulungen
            </Link>
          </div>

          {/* Desktop CTA */}
          <div className="hidden xl:flex items-center gap-3">
            <a href="tel:+491637917044" className="hidden 2xl:flex items-center gap-1.5 text-sm text-slate-500 hover:text-cyan-700 transition-colors whitespace-nowrap">
              <Phone className="h-4 w-4" /><span>0163 791 7044</span>
            </a>
            {isAlltag ? (
              <Link to="/alltagsbegleitung#anfrage" data-testid="header-cta-btn"
                className="bg-green-600 hover:bg-green-700 text-white rounded-xl px-6 py-2.5 text-[15px] font-semibold shadow-sm hover:shadow-md transition-all inline-flex items-center">
                Alltagsbegleitung anfragen
              </Link>
            ) : (
              <Button data-testid="header-cta-btn" onClick={() => scrollTo("#termin-buchen")}
                className="bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl px-6 py-2.5 text-[15px] font-semibold shadow-sm hover:shadow-md transition-all">
                Jetzt Termin buchen
              </Button>
            )}
          </div>

          {/* Mobile menu button */}
          <button data-testid="mobile-menu-btn" onClick={() => setMobileOpen(!mobileOpen)}
            className="xl:hidden p-2 rounded-lg hover:bg-slate-100 transition-colors">
            {mobileOpen ? <X className="h-6 w-6 text-slate-700" /> : <Menu className="h-6 w-6 text-slate-700" />}
          </button>
        </div>

        {/* Mobile: Compact switcher bar below header row */}
        <MobileSwitcherBar isAlltag={isAlltag} />

        {/* Mobile menu */}
        {mobileOpen && (
          <div data-testid="mobile-menu" className="xl:hidden pb-6 border-t border-slate-100 animate-fade-in">
            <nav className="flex flex-col pt-4 gap-1">
              {/* Finder */}
              <Link to="/blog/pflegeleistungen-finder" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-finder"
                className="flex items-center gap-2 mx-2 mb-2 px-4 py-3.5 text-base font-semibold text-cyan-700 bg-cyan-50 border border-cyan-200 rounded-xl transition-all">
                <Search className="h-5 w-5" /> Leistungen prüfen
              </Link>

              {/* Nav links */}
              {!isAlltag && pflegeberatungLinks.map((link) => (
                <button key={link.href} onClick={() => scrollTo(link.href)}
                  className="text-left px-4 py-3 text-base font-medium text-slate-700 hover:text-cyan-700 hover:bg-cyan-50/60 rounded-xl transition-all">
                  {link.label}
                </button>
              ))}

              <Link to="/blog" onClick={() => setMobileOpen(false)}
                className="text-left px-4 py-3 text-base font-medium text-slate-700 hover:text-cyan-700 hover:bg-cyan-50/60 rounded-xl">
                Ratgeber
              </Link>
              <Link to="/schulungen" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-schulungen"
                className="flex items-center gap-2 text-left px-4 py-3 text-base font-medium text-slate-700 hover:text-cyan-700 hover:bg-cyan-50/60 rounded-xl">
                <GraduationCap className="h-5 w-5 text-cyan-700" /> Schulungen für Firmen
              </Link>
              <Link to="/mitarbeiter/login" onClick={() => setMobileOpen(false)} data-testid="mobile-nav-mitarbeiter"
                className="flex items-center gap-2 text-left px-4 py-3 text-base font-medium text-slate-700 hover:text-cyan-700 hover:bg-cyan-50/60 rounded-xl">
                <LockKeyhole className="h-5 w-5 text-cyan-700" /> Mitarbeiter-Login
              </Link>

              <div className="mt-3 px-4">
                {isAlltag ? (
                  <Link to="/alltagsbegleitung#anfrage" onClick={() => setMobileOpen(false)}
                    className="block w-full bg-green-600 hover:bg-green-700 text-white rounded-xl py-3 text-base font-semibold text-center">
                    Alltagsbegleitung anfragen
                  </Link>
                ) : (
                  <Button data-testid="mobile-cta-btn" onClick={() => scrollTo("#termin-buchen")}
                    className="w-full bg-cyan-700 hover:bg-cyan-600 text-white rounded-xl py-3 text-base font-semibold">
                    Jetzt Termin buchen
                  </Button>
                )}
              </div>
            </nav>
          </div>
        )}
      </div>
    </header>
  );
}
